import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { cn } from "@nextui-org/react";
import { BreachHistoryColumns } from "../constants/breachHistoryColumns";
import { useServerSideState } from "../hooks/useServerSideState";
import { useGetBreachHistory } from "../requests/getBreachHistory";
import { BreachHistoryItem } from "../types/breachHistory";
import { SearchField } from "@/shared/components/TableSearch";


export function BreachHistoryTab() {
  // Server-side state management (pagination, search, filters)
  const { queryParams, pagination, search, filters } = useServerSideState({
    page: 1,
    page_size: 25,
  });

  // Fetch breach history data from API
  const { data, isLoading, isFetching, isError, error } = useGetBreachHistory({
    ...queryParams,
  });

  // Extract data from API response
  const breaches = data?.breaches ?? [];
  const totalBreaches = data?.total_breaches ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden mb-4">
      {/* Table Section */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Header: Title + Count | Search */}
        <div className="flex flex-row items-center gap-2 mb-4 flex-shrink-0">
          {/* Left: Title + Count */}
          <div className="flex flex-row items-center gap-2 flex-grow">
            <h3 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
              Breach History
            </h3>
            <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
              <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                {totalBreaches}
              </span>
            </div>
          </div>
          
          {/* Right: Search */}
          <SearchField
            value={search.searchTerm}
            onChange={search.setSearchTerm}
            placeholder="Search by name"
            className="w-[420px]"
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <div className="relative flex-1 min-h-0">
            {/* Loading overlay - only during refetch, not initial load */}
            {!isLoading && isFetching && (
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                <LoadingSpinner />
              </div>
            )}
            <ZTableDeprecrated<BreachHistoryItem>
            className={cn("border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto flex-1")}
            layout="auto"
            loading={isLoading || isFetching}
            rows={breaches}
            columns={BreachHistoryColumns}
            isHeaderSticky
            isCompact
            removeWrapper
            getRowId={(row) => row.id}
            tableHeaderProps={{ className: "shadow-none rounded-none " }}
            tableRowProps={(row, rowIndex) => ({
              className: cn(rowIndex === 0 ? "border-none" : "border-y", "group"),
            })}
            classNames={{
              wrapper: "!shadow-none !rounded-none",
            }}
            aria-label="Breach History List"
            tableBodyProps={{
              emptyContent: isError ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-gray-500">Failed to load breach history</p>
                  {error && <p className="text-xs text-red-500">{error.message}</p>}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  {isLoading || isFetching ? (
                    <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
                  ) : (
                    <p className="text-sm text-gray-500">No breaches found</p>
                  )}
                </div>
              ),
            }}
            />
          </div>
          <ZTablePagination
            totalPages={totalPages}
            paginationState={{
              currentPage: currentPage,
              rowsPerPage: pagination.pageSize,
            }}
            setPagination={{
              handlePageChange: (page) => pagination.setPage(page),
              handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
            }}
          />
        </div>
      </div>
    </div>
  );
}
